import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchExamCDetail,
  fetchClassStudents,
  uploadExamCSubmission,
  deleteExamCSubmission,
  startExamCGrading,
} from "../api/client";
import type { ExamCDetail, ExamCSubmission, StudentSummary } from "../api/types";
import { Upload, Trash2, FileText, CheckCircle, AlertCircle, Play, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

const POLL_INTERVAL_MS = 2500;

type GradingState =
  | { status: "idle" }
  | { status: "starting" }
  | { status: "polling"; gradingId: number; papersGraded: number; totalPapers: number }
  | { status: "completed"; gradingId: number }
  | { status: "failed"; error: string };

export default function ExamCSetupPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation("pages");
  const examId = Number(sessionId);

  const [exam, setExam] = useState<ExamCDetail | null>(null);
  const [students, setStudents] = useState<StudentSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [uploadingFor, setUploadingFor] = useState<number | null>(null);
  const [strictness, setStrictness] = useState("moderate");
  const [gradingState, setGradingState] = useState<GradingState>({ status: "idle" });

  const reload = useCallback(() => {
    fetchExamCDetail(examId)
      .then(setExam)
      .catch((e) => setLoadError(e.message));
  }, [examId]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (!exam?.class_id) return;
    fetchClassStudents(exam.class_id)
      .then(setStudents)
      .catch(() => setStudents([]));
  }, [exam?.class_id]);

  const submissionFor = (studentId: number): ExamCSubmission | undefined =>
    exam?.submissions.find((s) => s.student_id === studentId);

  const handleUpload = async (studentId: number, files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadingFor(studentId);
    try {
      await uploadExamCSubmission(examId, studentId, Array.from(files));
      reload();
    } catch (err) {
      alert(err instanceof Error ? err.message : t("examCSetup.uploadError"));
    } finally {
      setUploadingFor(null);
    }
  };

  const handleDelete = async (subId: number) => {
    if (!confirm(t("examCSetup.deleteConfirm"))) return;
    try {
      await deleteExamCSubmission(examId, subId);
      reload();
    } catch (err) {
      alert(err instanceof Error ? err.message : t("examCSetup.deleteError"));
    }
  };

  const handleStartGrading = async () => {
    if (!exam) return;
    if (exam.submissions.length === 0) {
      alert(t("examCSetup.noPapersAlert"));
      return;
    }
    setGradingState({ status: "starting" });
    try {
      const { grading_id, total_papers } = await startExamCGrading(
        examId, strictness, exam.feedback_language
      );
      setGradingState({ status: "polling", gradingId: grading_id, papersGraded: 0, totalPapers: total_papers });
      pollProgress(grading_id);
    } catch (err: unknown) {
      setGradingState({ status: "failed", error: err instanceof Error ? err.message : t("examCSetup.gradingStartError") });
    }
  };

  const pollProgress = (gradingId: number) => {
    const tick = () => {
      fetch(`/grading/grading_progress/${gradingId}`, { credentials: "include" })
        .then((r) => r.json())
        .then((data) => {
          if (data.status === "completed") {
            setGradingState({ status: "completed", gradingId });
          } else if (data.status === "failed" || data.error) {
            setGradingState({ status: "failed", error: data.error || t("examCSetup.gradingError") });
          } else {
            setGradingState({ status: "polling", gradingId, papersGraded: data.papers_graded ?? 0, totalPapers: data.total_papers ?? 0 });
            setTimeout(tick, POLL_INTERVAL_MS);
          }
        })
        .catch(() => setTimeout(tick, POLL_INTERVAL_MS));
    };
    setTimeout(tick, POLL_INTERVAL_MS);
  };

  if (loadError) {
    return (
      <div style={{ padding: "16px", color: "var(--color-danger)" }}>
        {t("examCSetup.errorPrefix")} {loadError}
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="flex items-center gap-8" style={{ padding: "24px", color: "var(--text-muted)" }}>
        <span className="regrade-spinner" style={{ borderColor: "rgba(0,0,0,0.15)", borderTopColor: "var(--accent-copper)" }} />
        {t("examCSetup.loading")}
      </div>
    );
  }

  const allUploaded = students.length > 0 && students.every((s) => submissionFor(s.id));
  const gradingDisabled =
    gradingState.status === "starting" ||
    gradingState.status === "polling" ||
    gradingState.status === "completed";

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-16 mb-24">
        <button className="btn btn-ghost" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} />
          {t("common.back")}
        </button>
        <div>
          <h2 className="section-title">{exam.exam_name}</h2>
          <p className="text-sm text-muted mt-4">
            {exam.subject && <span>{exam.subject} · </span>}
            {exam.test_date && <span>{exam.test_date} · </span>}
            <span>{t("examCSetup.totalPoints", { n: exam.max_points })}</span>
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 760 }}>
        {/* Materials status */}
        {(exam.rubric_file_id || exam.questions_pdf_file_id) && (
          <div className="card card-padding mb-20">
            <div className="flex gap-16">
              {exam.rubric_file_id && (
                <div className="flex items-center gap-8" style={{ color: "var(--color-success)", fontSize: "0.85rem" }}>
                  <CheckCircle size={14} />
                  {t("examCSetup.rubricLoaded")}
                </div>
              )}
              {exam.questions_pdf_file_id && (
                <div className="flex items-center gap-8" style={{ color: "var(--color-success)", fontSize: "0.85rem" }}>
                  <CheckCircle size={14} />
                  {t("examCSetup.questionPaperLoaded")}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Student papers card */}
        <div className="card card-padding mb-20">
          <h3 className="card-title mb-20">{t("examCSetup.studentPapersCard")}</h3>

          {students.length === 0 ? (
            <p className="text-sm text-muted">
              {exam.class_id
                ? t("examCSetup.noStudentsInClass")
                : t("examCSetup.noClassSelected")}
            </p>
          ) : (
            <div>
              {students.map((student) => {
                const sub = submissionFor(student.id);
                const isUploading = uploadingFor === student.id;

                return (
                  <div key={student.id} className="upload-file-row">
                    <div className="upload-file-icon upload-file-icon-sm">
                      <FileText size={16} />
                    </div>
                    <div className="upload-file-info">
                      <div className="upload-file-name-compact">
                        <span className="upload-file-student">{student.full_name}</span>
                        {student.student_no && (
                          <span className="upload-file-filename">{student.student_no}</span>
                        )}
                      </div>
                    </div>

                    {sub ? (
                      <div className="flex items-center gap-8">
                        <CheckCircle size={14} style={{ color: "var(--color-success)", flexShrink: 0 }} />
                        <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                          {t("examCSetup.nPages", { count: sub.file_ids.length })}
                        </span>
                        <label
                          className="btn btn-outline btn-sm"
                          style={{ cursor: "pointer", fontSize: "0.75rem" }}
                          title={t("examCSetup.replacePapers")}
                        >
                          {t("examCSetup.replacePapersLabel")}
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            style={{ display: "none" }}
                            disabled={isUploading}
                            onChange={(e) => handleUpload(student.id, e.target.files)}
                          />
                        </label>
                        <button
                          className="btn btn-icon btn-ghost"
                          onClick={() => handleDelete(sub.id)}
                          title={t("examCSetup.deletePapersTitle")}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ) : (
                      <label
                        className="btn btn-outline btn-sm"
                        style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
                      >
                        {isUploading ? (
                          <span className="regrade-spinner" style={{ borderColor: "rgba(0,0,0,0.15)", borderTopColor: "var(--accent-copper)" }} />
                        ) : (
                          <Upload size={13} />
                        )}
                        {isUploading ? t("examCSetup.uploadLoading") : t("examCSetup.uploadButton")}
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          style={{ display: "none" }}
                          disabled={isUploading}
                          onChange={(e) => handleUpload(student.id, e.target.files)}
                        />
                      </label>
                    )}
                  </div>
                );
              })}

              <p className="text-sm text-muted mt-16">
                {t("examCSetup.nStudentsUploaded", { done: exam.submissions.length, total: students.length })}
                {allUploaded && " ✓"}
              </p>
            </div>
          )}
        </div>

        {/* Grading card */}
        <div className="card card-padding">
          <h3 className="card-title mb-20">{t("examCSetup.gradingCard")}</h3>

          <div className="form-row mb-20" style={{ maxWidth: 400 }}>
            <div className="form-group">
              <label className="form-label">{t("examCSetup.strictnessLabel")}</label>
              <select
                className="form-select"
                value={strictness}
                onChange={(e) => setStrictness(e.target.value)}
                disabled={gradingDisabled}
              >
                <option value="lenient">{t("common.strictness.flexible")}</option>
                <option value="moderate">{t("common.strictness.medium")}</option>
                <option value="strict">{t("common.strictness.strict")}</option>
              </select>
            </div>
          </div>

          {/* Progress / status messages */}
          {gradingState.status === "polling" && (
            <div
              className="flex items-center gap-8 mb-20"
              style={{ color: "var(--color-info)", fontSize: "0.875rem" }}
            >
              <span className="regrade-spinner" style={{ borderColor: "rgba(91,126,140,0.3)", borderTopColor: "var(--color-info)" }} />
              {t("examCSetup.gradingInProgress", { done: gradingState.papersGraded, total: gradingState.totalPapers })}
            </div>
          )}

          {gradingState.status === "completed" && (
            <div
              className="flex items-center gap-8 mb-20"
              style={{ color: "var(--color-success)", fontSize: "0.875rem" }}
            >
              <CheckCircle size={15} />
              {t("examCSetup.gradingDone")}{" "}
              <button
                className="btn btn-ghost btn-sm"
                style={{ color: "var(--accent-copper)", padding: "2px 6px" }}
                onClick={() => navigate(`/exams-c/${examId}`)}
              >
                {t("examCSetup.viewResults")}
              </button>
            </div>
          )}

          {gradingState.status === "failed" && (
            <div
              className="flex items-center gap-8 mb-20"
              style={{ color: "var(--color-danger)", fontSize: "0.875rem" }}
            >
              <AlertCircle size={15} />
              {gradingState.error}
            </div>
          )}

          <button
            className="btn btn-primary"
            onClick={handleStartGrading}
            disabled={gradingDisabled || exam.submissions.length === 0}
          >
            <Play size={14} />
            {t("examCSetup.startGrading")}
            {!allUploaded && students.length > 0 && (
              <span style={{ opacity: 0.7, fontSize: "0.78rem" }}>
                ({exam.submissions.length}/{students.length} {t("examCSetup.nPages", { count: exam.submissions.length }).split(" ")[1]})
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
